import {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
    type RefObject,
} from 'react';
import { useAtom } from 'jotai';
import { useIsDesktopViewport } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { Link } from 'react-router';
import { ArrowUpRight, ChevronRight } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import {
    linkifyHtml,
    RICH_TEXT_HTML_CLASS,
    splitLeadHtml,
} from '~/ui-rocco/pages/detail-shared';
import { htmlToPlainText } from '~/ui-rocco/pages/detail-shared/sentence-offsets';
import { useCropToImage } from '~/ui-rocco/pages/detail-shared/use-crop-to-image';
import { ensureProtocol } from '~/modules/url';
import { productDetailsExpandedAtom } from '../../state/details-expanded-atom';
import type {
    ProductBreadcrumb,
    ProductCustomSection,
    ProductInfoItem,
} from './product-info';

/**
 * The trail above the list, matched to the reference: 14px Roman, 16px between
 * steps, no separator glyphs, and the step you are on underlined. It sits 12px
 * clear of the list's first rule.
 *
 * The grey is #656565 rather than the reference's #b3b3b2, which came to 1.9:1
 * on the band's #f5f5f7 and so failed AA at this size. #656565 is 5.4:1; the
 * chevron between steps is allowed the lighter #767676, which still clears the
 * 3:1 a non-text glyph needs.
 */
const CRUMB_CLASS =
    'font-sans font-normal text-[#656565] no-underline transition-colors hover:text-[#323232] max-md:text-[13px] md:text-[14px]';

export const Breadcrumb = ({ trail }: { trail: ProductBreadcrumb[] }) => (
    <nav aria-label="Breadcrumb" className="mb-[12px]">
        <ol className="m-0 flex list-none flex-wrap items-center gap-[16px] p-0">
            {trail.map((step, index) => (
                <li
                    key={`${step.label}-${index}`}
                    className="flex min-w-0 items-center gap-[16px]"
                >
                    {index > 0 ? (
                        // The reference separates its steps with space alone,
                        // which read as nothing at all here — so the step is
                        // stated, in the trail's own grey.
                        <ChevronRight
                            aria-hidden="true"
                            className="size-[12px] shrink-0 text-[#767676]"
                        />
                    ) : null}
                    {step.to ? (
                        <Link to={step.to} className={CRUMB_CLASS}>
                            {step.label}
                        </Link>
                    ) : (
                        <span aria-current="page" className={CRUMB_CLASS}>
                            {step.label}
                        </span>
                    )}
                </li>
            ))}
        </ol>
    </nav>
);

/**
 * The spec list as stacked collapsibles, matched to the reference product page
 * (au.rains.com) it was asked to copy: a hairline over every row, the label and
 * its toggle pushed apart, 20px of padding above and below, and the value
 * beneath rather than in a second column.
 *
 * Measured off that page: 16px/600 label on #10100f in a 14.4px line box,
 * 14px/16.8px body, and a panel carrying no padding of its own — the gap under
 * an open value is the next row's own top padding. The rule is heavier than the
 * reference's #f5f5f5, which all but vanished on this page's tinted ground.
 *
 * One deviation, and it is a font limitation rather than a choice: the label is
 * the 500 cut. Neue Haas Grotesk ships no 600, and asking for one only gets a
 * synthetically emboldened 500 that renders heavier than the reference.
 */
/**
 * The project page's spec table, on the light ground: medium label left, grey
 * value right, a hairline under every row, set at the same 22px the project
 * sets its rows in — so the two pages' "beside the picture" sections read as
 * one section drawn twice.
 */
const ROW_CLASS =
    'flex items-start border-b border-black/10 first:pt-0 last:border-b-0 max-md:gap-4 max-md:py-2 md:gap-[3.0625rem] md:py-[0.8125rem]';
const LABEL_CLASS =
    'shrink-0 font-sans font-medium leading-[1.3] whitespace-pre-line text-[#10100f] max-md:w-[7.5rem] max-md:text-sm md:w-[16.1875rem] md:text-[1.375rem]';
/** Text track + fixed arrow track so unlinked rows align with text, not icons. */
const VALUE_CLASS =
    'm-0 grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_0.85em] items-baseline gap-1 break-words text-right font-sans font-normal leading-[1.3] text-[#767676] max-md:text-sm md:text-[1.375rem]';
/** The value's type alone, for a row whose content runs under its label. */
const VALUE_TYPE_CLASS =
    'font-sans font-normal leading-[1.3] text-[#767676] max-md:text-sm md:text-[1.375rem]';
const VALUE_HTML_CLASS = cn(
    VALUE_TYPE_CLASS,
    RICH_TEXT_HTML_CLASS,
    // Inside a value every line sits at the value size.
    '[&_h2]:text-[inherit] [&_h3]:text-[inherit] [&_h4]:text-[inherit]',
    // A list of one is a value, and a marker on it reads as a stray bullet —
    // which is why these were stripped. A list of several is a list, and
    // stripped it collapsed: thirteen features ran together as one block of
    // lines with nothing to separate or scan them by. Markers, indent and
    // leading come back only where there is a second item to tell apart.
    '[&_ol]:my-0 [&_ol]:list-none [&_ol]:pl-0',
    '[&_ul]:my-0 [&_ul]:list-none [&_ul]:pl-0',
    '[&_li+li]:mt-0',
    '[&_ol:has(>li+li)]:list-decimal [&_ol:has(>li+li)]:pl-[1.25rem]',
    '[&_ul:has(>li+li)]:list-disc [&_ul:has(>li+li)]:pl-[1.25rem]',
    '[&_ol:has(>li+li)>li+li]:mt-2 [&_ul:has(>li+li)>li+li]:mt-2',
    '[&_ol]:mt-[1rem] [&_p+p]:mt-[1rem] [&_ul]:mt-[1rem]',
    '[&_table]:w-full [&_td]:py-1',
);
const LINK_CLASS =
    'underline decoration-black/25 underline-offset-2 transition-colors hover:text-[#10100f]';
/** The Read more under the table and the description, in the project's grey. */
const READ_MORE_CLASS =
    'block cursor-pointer border-0 bg-transparent p-0 text-left font-sans font-normal leading-[1.3] text-[#767676] transition-colors hover:text-[#10100f] max-md:text-base md:text-[1.25rem]';
/** The description's first sentence and the rest, the rest also as plain text for the crop. */
const useDescription = (descriptionHtml: string | null | undefined) =>
    useMemo(() => {
        if (!descriptionHtml) return { lead: '', rest: '', plainText: '' };
        const { lead, rest } = splitLeadHtml(linkifyHtml(descriptionHtml));
        return { lead, rest, plainText: htmlToPlainText(rest) };
    }, [descriptionHtml]);

/** Visible text of a fragment, for comparing a heading against a label. */
const blockText = (html: string): string =>
    html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();

/**
 * CMS authors habitually open a section with its own title — a bold paragraph
 * repeating the tab name. The row already renders that title as its label, so
 * the copy would say it twice. Drops the leading block when it is the title and
 * nothing else; a section whose entire body is the title keeps it, since
 * removing it would leave the row empty.
 */
const stripLeadingTitle = (html: string, title: string): string => {
    const leading = html.match(/^\s*<(p|h[1-6])\b[^>]*>([\s\S]*?)<\/\1>\s*/i);
    if (!leading) return html;
    if (
        blockText(leading[2] ?? '').toLowerCase() !==
        blockText(title).toLowerCase()
    ) {
        return html;
    }
    const remainder = html.slice(leading[0].length);
    return blockText(remainder) ? remainder : html;
};

/** A value that is a bare URL — a clickable external link, not plain text. */
const isExternalUrl = (value: string) =>
    /^(?:https?:\/\/|www\.)\S+$/i.test(value.trim());

interface SpecRow {
    label: string;
    value: string;
    html?: string | null;
    node?: ReactNode;
    link?: string | null;
    newTab?: boolean;
}

/**
 * The column's opening, set as the project page sets its About: the
 * description's first sentence as the lead, 33px medium, then the rest as
 * 20px prose 92px under it — 24px and 16px on a phone.
 */
const LEAD_CLASS =
    'm-0 font-sans leading-[1.25] font-medium tracking-[-0.025em] text-[#10100f] max-md:text-[1.5rem] md:text-[2.0625rem]';
const PROSE_CLASS =
    'font-sans leading-[1.3] font-normal text-[#323232] max-md:text-base md:text-[1.25rem]';
const PROSE_GAP_CLASS = 'max-md:mt-4 md:mt-[5.75rem]';
/** The inline Read more on the cropped prose, continuing its sentence. */
const INLINE_READ_MORE_CLASS =
    'inline cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[#767676] transition-colors hover:text-[#10100f]';

/** The arrow a linked value carries, in a fixed column so unlinked rows line up. */
const LinkArrow = ({ show }: { show: boolean }) => (
    <span
        aria-hidden="true"
        className="inline-flex size-[0.85em] shrink-0 items-center justify-center"
    >
        {show ? (
            <ArrowUpRight
                strokeWidth={2}
                className="size-[0.85em] -translate-y-[0.05em]"
            />
        ) : null}
    </span>
);

/** A plain value: text, or a link with its arrow in the fixed column. */
const SpecValue = ({ row }: { row: SpecRow }): ReactNode => {
    if (row.link) {
        return (
            <Link
                to={row.link}
                target={row.newTab ? '_blank' : undefined}
                rel={row.newTab ? 'noopener noreferrer' : undefined}
                className={cn(LINK_CLASS, 'contents')}
            >
                <span className="min-w-0 text-right whitespace-pre-line">
                    {row.value}
                </span>
                <LinkArrow show />
            </Link>
        );
    }
    if (isExternalUrl(row.value)) {
        return (
            <a
                href={ensureProtocol(row.value.trim())}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(LINK_CLASS, 'contents')}
            >
                <span className="min-w-0 text-right">{row.value}</span>
                <LinkArrow show />
            </a>
        );
    }
    return (
        <>
            <span className="min-w-0 text-right whitespace-pre-line">
                {row.value}
            </span>
            <LinkArrow show={false} />
        </>
    );
};

/**
 * One row of the table: the label and, beside it, the value. A value that is
 * copy rather than a fact — a CMS tab, the file list — runs under its label
 * instead, at the same size, since a paragraph ranged right against a label
 * reads as a mistake.
 */
const SpecTableRow = ({ row, hidden }: { row: SpecRow; hidden: boolean }) => {
    const runsUnder = !!row.node || !!row.html;
    return (
        <div className={cn(ROW_CLASS, runsUnder && 'flex-col')} hidden={hidden}>
            <dt className={cn(LABEL_CLASS, runsUnder && 'w-full')}>
                {row.label}
            </dt>
            {row.node ? (
                <dd className={cn('m-0 mt-[0.5rem] w-full', VALUE_TYPE_CLASS)}>
                    {row.node}
                </dd>
            ) : row.html ? (
                <dd
                    className={cn('m-0 mt-[0.5rem] w-full', VALUE_HTML_CLASS)}
                    dangerouslySetInnerHTML={{
                        __html: linkifyHtml(row.html),
                    }}
                />
            ) : (
                <dd className={VALUE_CLASS}>
                    <SpecValue row={row} />
                </dd>
            )}
        </div>
    );
};

export interface ProductSpecListProps {
    items: ProductInfoItem[];
    /**
     * The product's own account of itself, opening the column as the
     * project's description opens its About.
     */
    descriptionHtml?: string | null;
    scopeOfUseHtml?: string | null;
    customSections?: ProductCustomSection[];
    /**
     * The picture beside the column. The column stops at its foot, as the
     * project's does: a description that runs past it is cut at a sentence
     * end with Read more on its heels and the list waits behind it; one that
     * fits is followed by the rows that fit, whole, and Read more under them.
     */
    fitToRef?: RefObject<HTMLElement | null>;
}

/** Room for the Read more line under the last row that fits. */
const READ_MORE_ROOM_PX = 56;

/**
 * How many rows fit beside the picture, measured rather than guessed.
 *
 * Every row stands open, so the list's height is whatever the supplier
 * wrote, and the picture's is whatever the row makes a square. The rows are
 * laid out in full, and before paint each one's foot is compared with the
 * picture's: the last row that ends above it, with a line to spare for Read
 * more, is the last one shown. Rows are whole or hidden — never cut mid-row.
 * The picture is watched, and a resize measures again. Null means every row
 * shows: below md, once the reader has asked for the rest, or when they all
 * fit.
 */
const useRowsThatFit = (
    listRef: RefObject<HTMLDListElement | null>,
    fitToRef: RefObject<HTMLElement | null> | undefined,
    enabled: boolean,
    rowCount: number,
) => {
    const [visible, setVisible] = useState<number | null>(null);
    const [pass, setPass] = useState(0);

    useEffect(() => {
        const frame = fitToRef?.current;
        if (!enabled || !frame) return;
        const observer = new ResizeObserver(() => setPass((n) => n + 1));
        observer.observe(frame);
        return () => observer.disconnect();
    }, [enabled, fitToRef]);

    useLayoutEffect(() => {
        const list = listRef.current;
        const frame = fitToRef?.current;
        if (!enabled || !list || !frame) {
            setVisible(null);
            return;
        }
        // The frame is sticky, so its box may have travelled; its cell has
        // not, and the frame's natural foot is the cell's top plus its own
        // height.
        const cellTop = (frame.parentElement ?? frame).getBoundingClientRect()
            .top;
        const foot = cellTop + frame.getBoundingClientRect().height;
        // Measure the WHOLE list, not just the rows a previous pass left
        // showing — measuring survivors only would let the cut shrink on
        // resize but never grow back. Hidden rows have no box, so the full
        // list is laid out for the measurement and the attribute restored
        // before paint (this is a layout effect); a changed verdict then
        // re-renders the right set.
        const rows = [...list.children].filter(
            (child): child is HTMLElement => child instanceof HTMLElement,
        );
        const wasHidden = rows.map((row) => row.hidden);
        rows.forEach((row) => {
            row.hidden = false;
        });
        let fits = 0;
        for (const row of rows) {
            if (row.getBoundingClientRect().bottom + READ_MORE_ROOM_PX > foot) {
                break;
            }
            fits += 1;
        }
        rows.forEach((row, index) => {
            row.hidden = wasHidden[index] ?? false;
        });
        // The first row always shows, and a list that fits whole needs no cut.
        setVisible(fits >= rowCount ? null : Math.max(1, fits));
    }, [enabled, listRef, fitToRef, rowCount, pass]);

    return visible;
};

export const ProductSpecList = ({
    items,
    descriptionHtml,
    scopeOfUseHtml,
    customSections = [],
    fitToRef,
}: ProductSpecListProps) => {
    const desktop = useIsDesktopViewport();
    const [expanded, setExpanded] = useAtom(productDetailsExpandedAtom);
    const listRef = useRef<HTMLDListElement>(null);
    const noFrame = useRef<HTMLElement>(null);
    const { lead, rest, plainText } = useDescription(descriptionHtml);
    const cropping = desktop && !expanded && !!fitToRef;
    const { copyRef, proseRef, twinRef, limit, crop } = useCropToImage(
        fitToRef ?? noFrame,
        cropping,
        plainText,
    );
    const measuring = cropping && crop === null;
    // The description itself runs past the picture: cut at a sentence end,
    // and the list waits behind Read more with the rest of it.
    const descriptionCut =
        cropping && crop !== null && crop.at < plainText.length;

    const rows: SpecRow[] = [
        ...items,
        ...(scopeOfUseHtml
            ? [{ label: 'Scope of use', value: '', html: scopeOfUseHtml }]
            : []),
        ...customSections
            .map((section) => ({
                label: section.title,
                value: '',
                html: stripLeadingTitle(section.html, section.title),
            }))
            // A tab saved twice in the CMS is the same row printed twice.
            // Compared on the text the reader is left with rather than the
            // markup behind it: the copies differ by the heading stripped
            // just above and by whitespace nobody can see. Two tabs that
            // share a title and say different things both keep their place.
            .filter(
                (row, index, all) =>
                    all.findIndex(
                        (other) =>
                            other.label === row.label &&
                            blockText(other.html ?? '') ===
                                blockText(row.html ?? ''),
                    ) === index,
            ),
    ];

    const visible = useRowsThatFit(
        listRef,
        fitToRef,
        cropping && !descriptionCut,
        rows.length,
    );

    // Whether collapsing would put anything away, remembered from the last
    // time the column was collapsed — expanded, the measurements that decide
    // it are switched off and cannot be consulted.
    //
    // The column does not own the expanded state: the statement at the top of
    // the page sends the reader here and opens it on the way, whether or not
    // this column was ever cut. A column short enough to fit beside its
    // picture was then offering a Read less that had nothing to put back.
    const [collapsible, setCollapsible] = useState(false);
    useEffect(() => {
        if (expanded) return;
        setCollapsible(descriptionCut || visible !== null);
    }, [expanded, descriptionCut, visible]);

    if (rows.length === 0 && !descriptionHtml) return null;

    return (
        <div className="relative">
            {/* The plain text at the column's width, which is what the
                sentence ends are measured against. Off the page's own flow,
                and never shown. */}
            {measuring && plainText ? (
                <p
                    ref={twinRef}
                    aria-hidden="true"
                    className={cn(
                        PROSE_CLASS,
                        'pointer-events-none absolute top-0 left-0 m-0 w-full whitespace-pre-line opacity-0',
                    )}
                >
                    {plainText}
                </p>
            ) : null}
            <div
                ref={copyRef}
                style={
                    measuring && limit !== null
                        ? { maxHeight: limit }
                        : undefined
                }
                className={cn(measuring && 'overflow-hidden')}
            >
                {lead ? <p className={LEAD_CLASS}>{lead}</p> : null}
                {descriptionCut && crop ? (
                    // The description to the end of a sentence, and Read more
                    // on the same line: the toggle continues the sentence
                    // rather than sitting under it as a control.
                    <p
                        ref={proseRef as React.RefObject<HTMLParagraphElement>}
                        className={cn(
                            PROSE_CLASS,
                            lead && PROSE_GAP_CLASS,
                            'm-0 whitespace-pre-line',
                        )}
                    >
                        {plainText.slice(0, crop.at).trimEnd()}{' '}
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            className={INLINE_READ_MORE_CLASS}
                        >
                            Read more
                        </button>
                    </p>
                ) : rest ? (
                    <div
                        ref={proseRef as React.RefObject<HTMLDivElement>}
                        className={cn(
                            PROSE_CLASS,
                            RICH_TEXT_HTML_CLASS,
                            lead && PROSE_GAP_CLASS,
                        )}
                        dangerouslySetInnerHTML={{ __html: rest }}
                    />
                ) : null}
                {/* Keyed by position as well as label: a supplier can give
                    two CMS tabs the same title, and on the label alone React
                    saw one key twice and stopped reconciling those rows. */}
                {!descriptionCut && rows.length > 0 ? (
                    <dl
                        ref={listRef}
                        className={cn(
                            'm-0',
                            (lead || rest) && 'max-md:mt-8 md:mt-[3.75rem]',
                        )}
                    >
                        {rows.map((row, index) => (
                            <SpecTableRow
                                key={`${row.label}-${index}`}
                                row={row}
                                hidden={visible !== null && index >= visible}
                            />
                        ))}
                    </dl>
                ) : null}
                {!descriptionCut && visible !== null ? (
                    <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        aria-expanded={false}
                        className={cn(READ_MORE_CLASS, 'mt-[1.5rem]')}
                    >
                        Read more
                    </button>
                ) : null}
                {expanded && desktop && collapsible ? (
                    // The way back, at the foot of everything Read more let
                    // out, in the same voice.
                    <button
                        type="button"
                        onClick={() => setExpanded(false)}
                        aria-expanded
                        className={cn(READ_MORE_CLASS, 'mt-[1.5rem]')}
                    >
                        Read less
                    </button>
                ) : null}
            </div>
        </div>
    );
};
